import { c as _c } from "react/compiler-runtime";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@osmix/ui/button";
import { Select } from "@/components/ui/select";
import { TableHead } from "@/components/ui/table";
import type { DataTable, SortState } from "@/lib/use-data-table";
import { cn } from "@/lib/utils";
const PAGE_SIZES = [10, 25, 50, 100] as const;

// A clickable column header that drives a useDataTable sort. The icon reflects
// state: neutral when another column is active, up/down when this one is.
export function SortableHead(t0) {
  const $ = _c(16);
  const {
    label,
    sortKey,
    sort,
    onToggle,
    align: t1,
    className
  } = t0;
  const align = t1 === undefined ? "left" : t1;
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  let t2;
  if ($[0] !== onToggle || $[1] !== sortKey) {
    t2 = () => onToggle(sortKey);
    $[0] = onToggle;
    $[1] = sortKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = active ? "text-foreground" : "text-muted-foreground";
  const t4 = align === "right" && "flex-row-reverse";
  let t5;
  if ($[3] !== t3 || $[4] !== t4) {
    t5 = cn("inline-flex items-center gap-1 transition-colors hover:text-foreground", t3, t4);
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== Icon) {
    t6 = <Icon className="size-3.5 shrink-0" />;
    $[6] = Icon;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== label || $[9] !== t2 || $[10] !== t5 || $[11] !== t6) {
    t7 = <button type="button" onClick={t2} className={t5}>{label}{t6}</button>;
    $[8] = label;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== className || $[14] !== t7) {
    t8 = <TableHead className={className}>{t7}</TableHead>;
    $[13] = className;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

// Page-size select + range readout + prev/next, driven by a useDataTable.
// Generic over the table's row/key types so it pairs with any list.
export function DataTablePagination(t0) {
  const $ = _c(56);
  const {
    table
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    page,
    pageCount,
    total,
    rangeStart,
    rangeEnd,
    pageSize,
    setPage,
    setPageSize
  } = table;
  let t1;
  if ($[0] !== t) {
    t1 = t("pagination.rowsPerPage");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span>{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== setPageSize) {
    t3 = e => setPageSize(Number(e.target.value));
    $[4] = setPageSize;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = PAGE_SIZES.map(_temp);
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== pageSize || $[8] !== t3) {
    t5 = <Select value={pageSize} onChange={t3} className="h-8 w-[4.5rem]">{t4}</Select>;
    $[7] = pageSize;
    $[8] = t3;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <div className="flex items-center gap-2">{t2}{t5}</div>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== rangeEnd || $[14] !== rangeStart || $[15] !== t || $[16] !== total) {
    t7 = total === 0 ? t("pagination.empty") : t("pagination.range", {
      from: rangeStart,
      to: rangeEnd,
      total
    });
    $[13] = rangeEnd;
    $[14] = rangeStart;
    $[15] = t;
    $[16] = total;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = <span className="tabular-nums">{t7}</span>;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== page || $[21] !== setPage) {
    t9 = () => setPage(page - 1);
    $[20] = page;
    $[21] = setPage;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = page <= 1;
  let t11;
  if ($[23] !== t) {
    t11 = t("pagination.prev");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronLeft className="size-4" />;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t9) {
    t13 = <Button variant="outline" size="icon" className="size-8" onClick={t9} disabled={t10} aria-label={t11}>{t12}</Button>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== page || $[31] !== pageCount || $[32] !== t) {
    t14 = t("pagination.page", {
      page,
      pageCount
    });
    $[30] = page;
    $[31] = pageCount;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14) {
    t15 = <span className="px-1 tabular-nums">{t14}</span>;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== page || $[37] !== setPage) {
    t16 = () => setPage(page + 1);
    $[36] = page;
    $[37] = setPage;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = page >= pageCount;
  let t18;
  if ($[39] !== t) {
    t18 = t("pagination.next");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ChevronRight className="size-4" />;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t16 || $[43] !== t17 || $[44] !== t18) {
    t20 = <Button variant="outline" size="icon" className="size-8" onClick={t16} disabled={t17} aria-label={t18}>{t19}</Button>;
    $[42] = t16;
    $[43] = t17;
    $[44] = t18;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t13 || $[47] !== t15 || $[48] !== t20) {
    t21 = <div className="flex items-center gap-1">{t13}{t15}{t20}</div>;
    $[46] = t13;
    $[47] = t15;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t21 || $[51] !== t8) {
    t22 = <div className="flex items-center gap-4">{t8}{t21}</div>;
    $[50] = t21;
    $[51] = t8;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22 || $[54] !== t6) {
    t23 = <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">{t6}{t22}</div>;
    $[53] = t22;
    $[54] = t6;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  return t23;
}
function _temp(n) {
  return <option key={n} value={n}>{n}</option>;
}